import { Link } from "react-router-dom";
import {
  Nav,
  Footer,
  TrustStrip,
  CtaBlock,
  usePageSeo,
  YARD_IMG_URL,
  AGRI_IMG_URL,
  RIGID_PLASTICS_IMG_URL,
  SECURE_IMG_URL,
  CARDBOARD_IMG_URL,
  RESERVOIR_COMPLETE_URL,
  AGG_SOIL_SCREENING_URL,
} from "../layout/shared";

const SERVICES = [
  {
    kicker: "Farm plastics · packaging",
    title: "Recycling",
    body:
      "Farm plastic and packaging collections built around rural sites. Silage and bale wrap, pit sheets, agricultural film, fertiliser and feed sacks, cardboard and selected rigid plastics.",
    link: "/agricultural-recycling",
    linkLabel: "Explore farm plastic recycling",
    img: AGRI_IMG_URL,
  },
  {
    kicker: "Volume · scheduled",
    title: "Cardboard recycling",
    body:
      "One-off clearances and scheduled collections for suitable dry, segregated cardboard from farms, businesses, retail units and recycling sites.",
    link: "/cardboard-recycling",
    linkLabel: "Ask about cardboard collections",
    img: CARDBOARD_IMG_URL,
  },
  {
    kicker: "Rigid plastics · clean EPS",
    title: "Rigid plastics & polystyrene",
    body:
      "Collection, sorting, shredding and granulation of suitable rigid plastics. Clean EPS polystyrene is also assessed as a separate material stream.",
    link: "/polystyrene-recycling",
    linkLabel: "Explore plastics & polystyrene recycling",
    img: RIGID_PLASTICS_IMG_URL,
  },
  {
    kicker: "Confidential · sensitive",
    title: "Secure destruction",
    body:
      "Controlled destruction for confidential documents, branded stock, recalled or obsolete products and other sensitive materials, with an agreed evidence package.",
    link: "/secure-destruction",
    linkLabel: "Explore secure destruction",
    img: SECURE_IMG_URL,
  },
  {
    kicker: "Rural infrastructure",
    title: "Reservoirs",
    body:
      "One coordinated route from initial feasibility through planning, specialist engineering, material balance, earthworks, construction and aftercare.",
    link: "/farm-reservoirs",
    linkLabel: "Explore farm reservoir support",
    img: RESERVOIR_COMPLETE_URL,
  },
  {
    kicker: "Assessment · feasibility",
    title: "Aggregates",
    body:
      "Evidence-led route assessment for soil, stone and hardcore leaving a site, plus feasibility reviews for proposed mobile-processing projects.",
    link: "/aggregates",
    linkLabel: "Check a material or mobile-project route",
    img: AGG_SOIL_SCREENING_URL,
  },
];

const COLLECTION_OPTIONS = [
  "A company-operated cage vehicle for suitable smaller and lighter loads.",
  "A compactor collection arranged for suitable steady-volume material.",
  "A hook-loader or RoRo movement arranged for suitable bulk clearances.",
  "Collection by an agreed third-party haulier.",
  "Customer delivery by written booking, once the receiving site and material are confirmed.",
];

const Home = () => {
  usePageSeo({
    title:
      "Recycling & Rural Services · 3 Counties Recycling",
    description:
      "Practical recycling and rural services across Worcestershire, Herefordshire and Gloucestershire. See the services currently available from 3 Counties Recycling.",
    path: "/",
  });

  return (
    <main data-testid="home-root" className="bg-white text-[#1a1a1a]">
      <Nav showIcons={false} />

      {/* Hero */}
      <section
        id="top"
        className="relative overflow-hidden bg-[#024120] text-white"
      >
        <img
          src={YARD_IMG_URL}
          alt="Cardboard bales being stacked at the 3 Counties Recycling yard"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#024120] via-[#024120]/85 to-transparent md:via-[#024120]/75" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#024120]/60" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-28 md:pb-40 min-h-[560px] md:min-h-[640px] flex items-end">
          <div className="max-w-2xl rise">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#FF6600]">
              Recycling · material recovery · rural infrastructure
            </p>
            <h1
              data-testid="home-h1"
              className="font-display font-black text-[42px] leading-[1.02] sm:text-5xl lg:text-[68px] mt-5 tracking-tight drop-shadow-[0_2px_18px_rgba(0,0,0,0.35)]"
            >
              Practical recycling and{" "}
              <span className="text-[#46B649]">rural services</span> across
              three counties.
            </h1>
            <p className="mt-7 max-w-xl text-base sm:text-lg text-white/85 leading-relaxed">
              3 Counties Recycling helps farms, businesses and contractors
              assess recycling, material recovery and rural infrastructure
              work across Worcestershire, Herefordshire and Gloucestershire.
              We check the material, site, authorisations and evidence
              needed before work begins.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link
                to="/get-a-quote"
                data-testid="home-hero-quote-cta"
                className="group inline-flex items-center justify-center gap-3 bg-[#FF6600] hover:bg-[#E55A00] text-white font-bold px-7 py-4 transition-all hover:-translate-y-1"
              >
                Get a quote
              </Link>
              <a
                href="#services"
                data-testid="home-hero-explore-cta"
                className="inline-flex items-center justify-center gap-3 border-2 border-white/40 hover:border-white text-white font-bold px-7 py-4 transition-all hover:bg-white/10 backdrop-blur-sm"
              >
                Explore our services
              </a>
            </div>
            <div className="mt-10">
              <TrustStrip variant="dark" showIcons={false} />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
            <div className="md:col-span-7">
              <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
                / What we can do
              </p>
              <h2 className="font-display font-black text-4xl md:text-5xl lg:text-[56px] tracking-tight mt-3 leading-[1.05]">
                Practical services
                <br />
                <span className="text-[#024120]">
                  for material and rural projects.
                </span>
              </h2>
            </div>
            <p className="md:col-span-5 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
              Our work sits where agriculture, industry and resource recovery
              meet. We start by understanding the material, site and
              commercial objective, then confirm the right route before work
              begins.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <Link
                key={s.title}
                to={s.link}
                data-testid={`home-service-${s.title
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")}`}
                className="group bg-white border border-[#024120]/10 overflow-hidden hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(13,42,32,0.4)] hover:border-[#46B649] transition-all duration-300 flex flex-col"
              >
                <div className="relative aspect-[16/9] bg-[#024120] overflow-hidden">
                    <img
                      src={s.img}
                      alt={s.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#46B649]">
                    {s.kicker}
                  </p>
                  <h3 className="mt-2 font-display font-black text-2xl text-[#024120] tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#4A4A4A] flex-1">
                    {s.body}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#024120] group-hover:text-[#FF6600] transition-colors">
                    {s.linkLabel}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Collection options (replaces old "our fleet" claim) */}
      <section id="collection-options" className="bg-[#F2F2F2] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
            <div className="md:col-span-7">
              <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
                / Collection options
              </p>
              <h2 className="font-display font-black text-4xl md:text-5xl lg:text-[52px] tracking-tight mt-3 leading-[1.05]">
                Matched to the material,
                <br />
                <span className="text-[#024120]">site and compliance route.</span>
              </h2>
            </div>
            <p className="md:col-span-5 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
              From a smaller loose collection to a full yard clearance or
              bulk lift, the vehicle and loading arrangement should be
              chosen around the material, volume, access and compliance route.
              Tell us what has built up and how the site can be reached.
              We will confirm the proposed collection method and price
              before booking.
            </p>
          </div>

          <ul className="grid md:grid-cols-2 gap-5">
            {COLLECTION_OPTIONS.map((opt, i) => (
              <li
                key={opt}
                data-testid={`home-collection-option-${i + 1}`}
                className="flex items-start gap-5 bg-white border-l-4 border-[#46B649] px-6 py-5"
              >
                <span className="text-[#024120] text-[15px] leading-relaxed">
                  {opt}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Value / three counties / start with the job */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-8">
          {[
            {
              t: "Keep the value of material local",
              d: "Heavy or bulky material becomes expensive when it is moved without a plan. We look at the whole material journey and protect as much useful value as the material genuinely allows.",
            },
            {
              t: "Built around the three counties",
              d: "Worcestershire, Herefordshire and Gloucestershire are connected rural and commercial economies. Local focus helps us assess journeys, access, collection economics, outlets and project support.",
            },
            {
              t: "Start with the job, not a price list",
              d: "Send the postcode, photographs, quantity, access information, date, waste codes or drawings if you have them. We tell you what can be quoted immediately and what needs further checking.",
            },
          ].map((c) => (
            <div
              key={c.t}
              className="border border-[#024120]/10 p-8 flex flex-col"
            >
              <h3 className="font-display font-bold text-xl text-[#024120] tracking-tight">
                {c.t}
              </h3>
              <p className="mt-3 text-[15px] text-[#4A4A4A] leading-relaxed">
                {c.d}
              </p>
            </div>
          ))}
        </div>
      </section>

      <CtaBlock
        showIcons={false}
        title="Tell us what you need to move, destroy, build or recover."
        body="One conversation is enough to start. We will assess the enquiry and give you a clear next step."
      />

      <Footer />
    </main>
  );
};

export default Home;
